import { Keyboard } from '@capacitor/keyboard'
import { UntitledIcon, type UntitledIconData } from '@faceup/icons'
import { UnstyledButton } from '@faceup/ui'
import { useElementSize } from '@mantine/hooks'
import { type ReactNode, useEffect, useState } from 'react'

type MobileFooterShellProps = {
  children: ReactNode
  itemCount: number
}

export const MobileFooterShell = ({ children, itemCount }: MobileFooterShellProps) => {
  const { ref, height } = useElementSize()
  const [isHidden, setIsHidden] = useState(false)

  useEffect(() => {
    const showHandle = Keyboard.addListener('keyboardWillShow', () => setIsHidden(true))
    const hideHandle = Keyboard.addListener('keyboardWillHide', () => setIsHidden(false))

    return () => {
      showHandle.then(h => h.remove())
      hideHandle.then(h => h.remove())
    }
  }, [])

  if (isHidden) {
    return null
  }

  return (
    <div
      className='w-full fixed bottom-0 border-t-1 bg-white border-t-[#E6ECF9]'
      style={{ height }}
    >
      <div
        ref={ref}
        className='grid justify-between w-full pb-[env(safe-area-inset-bottom)]'
        style={{
          gridTemplateColumns: `repeat(${itemCount}, minmax(0, 1fr))`,
        }}
      >
        {children}
      </div>
    </div>
  )
}

type ButtonItemProps = {
  onClick: () => void
  active: boolean
  icon: UntitledIconData
  children: ReactNode
}

export const ButtonItem = ({ icon, onClick, active, children }: ButtonItemProps) => (
  <UnstyledButton onClick={onClick}>
    <div
      className={`py-3 px-1 flex flex-col gap-1 text-center whitespace-nowrap overflow-hidden ${active ? 'text-colorPrimary' : ''}`}
    >
      <UntitledIcon icon={icon} size={20} />
      <span className='overflow-hidden text-ellipsis'>{children}</span>
    </div>
  </UnstyledButton>
)
